import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { writeSignal } from '@/lib/writeSignal';
import { isUnconfirmedWrite, saveFailureMessage } from '@/lib/apiErrors';
import { qk } from '@/lib/queryKeys';
import { formatPhDate, todayInPH } from '@/lib/phDate';
import type { ApiResponse, FarmResponse } from '@/types/api';
import type { SurvivalCheckCreate, SurvivalCheckInfo, SurvivalCheckResponse, SurvivalCheckUpdate } from '@/types/generated/openapi';

/** The farm's own survival sample size (owner setting), or null while unset or still loading. */
export function useFarmSurvivalSampleSize(farmId: string): number | null {
  const { data } = useQuery({
    queryKey: ['farm', farmId],
    queryFn: () => api.get<{ data: FarmResponse }>(`/api/v1/farms/${farmId}`),
    staleTime: 5 * 60 * 1000,
  });
  const size = data?.data?.settings?.survival_sample_size;
  return typeof size === 'number' && size > 0 ? size : null;
}

export function useSurvivalCheckMutations(farmId: string) {
  const queryClient = useQueryClient();
  const base = `/api/v1/farms/${farmId}/survival-checks`;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
    queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
  };
  const create = useMutation({
    // No retry: a create whose response was lost would be sent again and record the check twice.
    retry: false,
    mutationFn: (body: SurvivalCheckCreate) => api.post<ApiResponse<SurvivalCheckResponse>>(base, body, { signal: writeSignal() }),
    onSuccess: () => {
      toast.success('Survival check saved');
      refresh();
    },
    // A timeout may have committed: show the pond as it is now so the check isn't entered twice.
    onError: (e) => {
      if (isUnconfirmedWrite(e)) refresh();
    },
  });
  const update = useMutation({
    retry: false,
    mutationFn: ({ id, body }: { id: string; body: SurvivalCheckUpdate }) =>
      api.put<ApiResponse<SurvivalCheckResponse>>(`${base}/${id}`, body, { signal: writeSignal() }),
    onSuccess: () => {
      toast.success('Survival check updated');
      refresh();
    },
    // A timeout may have committed: show the pond as it is now, as create does.
    onError: (e) => {
      if (isUnconfirmedWrite(e)) refresh();
    },
  });
  const remove = useMutation({
    retry: false,
    mutationFn: (id: string) => api.delete(`${base}/${id}`, { signal: writeSignal() }),
    onSuccess: () => {
      toast.success('Survival check deleted');
      refresh();
    },
    onError: (e) => toast.error(saveFailureMessage(e, 'Not deleted')),
  });
  return { create, update, remove };
}

/**
 * Mirrors ensure_can_edit_entry: supervisors+ change any check; a technician only their own,
 * on the PH day the row was created. Delete is supervisor+ only.
 */
export function canEditSurvivalCheck(c: SurvivalCheckInfo, canSupervise: boolean, userId: string | undefined) {
  if (canSupervise) return true;
  return !!userId && c.technician_id === userId && formatPhDate(new Date(c.created_at)) === todayInPH();
}
